import React, { useEffect, useState } from 'react';
import {
   Box,
   Container,
   Typography,
   Paper,
   Button,
   CircularProgress,
   Alert,
   Divider,
   Chip,
   Stack,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   IconButton,
   Snackbar,
} from '@mui/material';
import {
   CheckCircle as CheckCircleIcon,
   Error as ErrorIcon,
   Event as EventIcon,
   CardTravel as TripIcon,
   AccessTime as TimeIcon,
   LocationOn as LocationIcon,
   Person as PersonIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Timestamp } from 'firebase/firestore';
import { formatDate } from '../../utils/dateUtils';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';

interface InvitationData {
   id: string;
   type: 'trip' | 'event';
   status: 'pending' | 'accepted' | 'declined' | 'expired';
   inviterId: string;
   inviterName: string;
   email: string;
   role: string;
   expiresAt: Timestamp;
   customMessage?: string;
   tripId?: string;
   tripTitle?: string;
   eventId?: string;
   eventTitle?: string;
   relationship?: {
      type: string;
      label?: string;
      notes?: string;
   };
}

interface EntityDetails {
   id: string;
   title: string;
   location?: {
      name?: string;
      address?: string;
      coordinates?: {
         latitude?: number;
         longitude?: number;
      };
   };
   startDate: Timestamp;
   endDate: Timestamp;
   createdBy: string;
}

const AcceptInvitationPage: React.FC = () => {
   const [searchParams] = useSearchParams();
   const token = searchParams.get('token');
   const invitationType = searchParams.get('type') || '';
   const invitationId = searchParams.get('invitationId') || '';
   const user_email = searchParams.get('user_email') || '';
   const { currentUser } = useUser();
   const { refreshInvitationStatus } = useSubscription();
   const navigate = useNavigate();

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);
   const [invitation, setInvitation] = useState<InvitationData | null>(null);
   const [entityDetails, setEntityDetails] = useState<EntityDetails | null>(null);
   const [processing, setProcessing] = useState(false);

   // Registration modal state
   const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [name, setName] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>(
      'success'
   );
   const [pendingAction, setPendingAction] = useState<'accept' | 'decline' | null>(null);

   useEffect(() => {
      if (!token) {
         setError('No invitation token provided');
         setLoading(false);
         return;
      }

      // Load invitation data
      loadInvitationData(invitationId || 'from-token', invitationType);
   }, [token, invitationId, invitationType]);

   const loadInvitationData = async (id: string, type: string) => {
      try {
         setError(null);

         if (!token) {
            setError('No invitation token provided');
            setLoading(false);
            return;
         }

         // Decode the JWT token to get invitation information
         let decodedToken;
         try {
            // Simple JWT decode (just for reading data, not for verification)
            const base64Payload = token.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            decodedToken = payload;
            console.log('Decoded invitation token:', decodedToken);
         } catch (err) {
            setError('Invalid invitation token');
            setLoading(false);
            return;
         }

         const invitationEmail = decodedToken.receiver_email;
         const entityId = decodedToken.tripId || decodedToken.eventId;
         const entityType = decodedToken.tripId ? 'trip' : 'event';
         const entityTitle = decodedToken.tripTitle || decodedToken.eventTitle;
         const realInvitationId = decodedToken.invitationId; // Get the real invitation ID from token

         if (!entityId || !realInvitationId) {
            setError('Invalid invitation data');
            setLoading(false);
            return;
         }

         // Create invitation data from JWT token
         const invitationData: InvitationData = {
            id: realInvitationId, // Use the real invitation ID from the token
            type: entityType as 'trip' | 'event',
            inviterId: 'unknown', // Add missing inviterId
            inviterName: decodedToken.sender || 'Someone',
            email: invitationEmail,
            role: decodedToken.role || 'editor', // Use role from JWT token, default to editor
            status: 'pending',
            expiresAt: null as any, // JWT tokens handle expiration
            customMessage: '',
            relationship: {
               type: 'friend',
               label: '',
               notes: '',
            },
            // Add specific fields based on entity type
            ...(entityType === 'trip'
               ? { tripId: entityId, tripTitle: entityTitle }
               : { eventId: entityId, eventTitle: entityTitle }),
         };

         setInvitation(invitationData);

         // Create entity details from JWT token data - no API calls needed for invitations
         const entityDetails: EntityDetails = {
            id: entityId,
            title: entityTitle || `Untitled ${entityType}`,
            location: undefined, // Location not available in JWT token
            startDate: Timestamp.fromDate(new Date()), // Default to current date
            endDate: Timestamp.fromDate(new Date()), // Default to current date
            createdBy: decodedToken.sender || 'Unknown',
         };

         setEntityDetails(entityDetails);

         setLoading(false);
      } catch (err) {
         console.error('Error loading invitation details:', err);
         setError('Failed to load invitation details');
      } finally {
         setLoading(false);
      }
   };

   const handleAcceptInvitation = async () => {
      if (!invitation) return;

      setPendingAction('accept');

      // If user is logged in, proceed directly
      if (currentUser?.uid) {
         await processInvitationAction('accept', invitation.email);
         return;
      }

      // Check if user exists by email
      await checkUserAndProceed('accept');
   };

   const handleDeclineInvitation = async () => {
      if (!invitation) return;

      setPendingAction('decline');

      // If user is logged in, proceed directly
      if (currentUser?.uid) {
         await processInvitationAction('decline', invitation.email);
         return;
      }

      // Check if user exists by email
      await checkUserAndProceed('decline');
   };

   const checkUserAndProceed = async (action: 'accept' | 'decline') => {
      if (!invitation) return;

      setProcessing(true);
      try {
         // Check if user exists
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email: invitation.email }),
            }
         );

         const result = await response.json();

         if (result.exists) {
            // User exists, they need to login first
            setSnackbarMessage('Please login to continue');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            setEmail(invitation.email);
            setRegistrationModalOpen(true);
         } else {
            // User doesn't exist, show registration modal
            setEmail(invitation.email);
            setRegistrationModalOpen(true);
         }
      } catch (err) {
         console.error('Error checking user:', err);
         setSnackbarMessage('Failed to process request. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setProcessing(false);
      }
   };

   const handleRegistration = async () => {
      if (!email.trim() || !password.trim() || !name.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         await processInvitationAction(pendingAction || 'accept', email, password, name);
         setRegistrationModalOpen(false);
      } catch (err) {
         console.error('Error registering:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleLogin = async () => {
      if (!email.trim() || !password.trim()) {
         setSnackbarMessage('Please enter your email and password');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Login user with Firebase
         await signInWithEmailAndPassword(auth, email, password);

         // After successful login, process the invitation
         await processInvitationAction(pendingAction || 'accept', email);
         setRegistrationModalOpen(false);
      } catch (err: any) {
         console.error('Error logging in:', err);
         setSnackbarMessage(err.message || 'Login failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const processInvitationAction = async (
      action: 'accept' | 'decline',
      userEmail: string,
      userPassword?: string,
      userName?: string
   ) => {
      if (!invitation) return;

      setProcessing(true);
      setError(null);

      try {
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/invitation/public-accept`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  invitationId: invitationId || invitation.id,
                  type: invitation.type,
                  email: userEmail,
                  password: userPassword,
                  name: userName,
                  action: action,
               }),
            }
         );

         const result = await response.json();

         if (result.success) {
            // If user just registered (has password), sign them in
            if (userPassword) {
               try {
                  await signInWithEmailAndPassword(auth, userEmail, userPassword);
                  // Refresh invitation status to ensure invited flag is loaded
                  await refreshInvitationStatus();
               } catch (loginErr) {
                  console.error('Error signing in after registration:', loginErr);
               }
            }

            setSuccess(result.message || `Invitation ${action}ed successfully!`);
            setSnackbarMessage(result.message || `Invitation ${action}ed successfully!`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            setTimeout(() => {
               // Always redirect to dashboard after accepting or declining
               navigate('/dashboard');
            }, 2000);
         } else {
            if (result.requiresRegistration) {
               setEmail(userEmail);
               setRegistrationModalOpen(true);
            } else {
               setSnackbarMessage(result.error || `Failed to ${action} invitation`);
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         }
      } catch (err) {
         console.error(`Error ${action}ing invitation:`, err);
         setSnackbarMessage(`Failed to ${action} invitation. Please try again.`);
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setProcessing(false);
      }
   };

   if (loading) {
      return (
         <Container maxWidth='sm'>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '60vh',
               }}
            >
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   if (error) {
      return (
         <Container maxWidth='sm'>
            <Alert
               severity='error'
               sx={{ mt: 4 }}
               action={
                  <Button color='inherit' size='small' onClick={() => navigate('/dashboard')}>
                     Try to login again
                  </Button>
               }
            >
               {error}
            </Alert>
         </Container>
      );
   }

   if (!invitation || !entityDetails) {
      return (
         <Container maxWidth='sm'>
            <Alert
               severity='error'
               sx={{ mt: 4 }}
               action={
                  <Button color='inherit' size='small' onClick={() => navigate('/dashboard')}>
                     Go Home
                  </Button>
               }
            >
               Invitation not found or has been removed
            </Alert>
         </Container>
      );
   }

   return (
      <Container maxWidth='sm'>
         <Box sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
               {success ? (
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 3,
                     }}
                  >
                     <CheckCircleIcon color='success' sx={{ fontSize: 60, mb: 2 }} />
                     <Typography variant='h6' gutterBottom>
                        {success}
                     </Typography>
                     <Typography color='text.secondary'>Redirecting...</Typography>
                  </Box>
               ) : (
                  <>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {invitation.type === 'trip' ? (
                           <TripIcon color='primary' sx={{ mr: 1 }} />
                        ) : (
                           <EventIcon color='primary' sx={{ mr: 1 }} />
                        )}
                        <Typography variant='h5'>
                           Invitation to join {invitation.type === 'trip' ? 'Trip' : 'Event'}
                        </Typography>
                     </Box>

                     <Divider sx={{ my: 2 }} />

                     <Stack spacing={2}>
                        <Typography variant='h6'>{entityDetails.title}</Typography>

                        <Box>
                           <Typography variant='body2' color='text.secondary'>
                              <TimeIcon fontSize='small' sx={{ verticalAlign: 'middle', mr: 1 }} />
                              {formatDate(entityDetails?.startDate)} -{' '}
                              {formatDate(entityDetails?.endDate)}
                           </Typography>

                           {entityDetails?.location?.name && (
                              <Typography variant='body2' color='text.secondary'>
                                 <LocationIcon
                                    fontSize='small'
                                    sx={{ verticalAlign: 'middle', mr: 1 }}
                                 />
                                 {entityDetails?.location?.name}
                              </Typography>
                           )}

                           <Typography variant='body2' color='text.secondary'>
                              <PersonIcon
                                 fontSize='small'
                                 sx={{ verticalAlign: 'middle', mr: 1 }}
                              />
                              Invited by {invitation?.inviterName}
                           </Typography>
                        </Box>

                        {invitation?.customMessage && (
                           <Box sx={{ my: 2 }}>
                              <Typography variant='body2' color='text.secondary'>
                                 Message from {invitation.inviterName}:
                              </Typography>
                              <Typography
                                 variant='body1'
                                 sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
                              >
                                 {invitation.customMessage}
                              </Typography>
                           </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                           <Chip
                              label={`Role: ${
                                 invitation?.role?.charAt(0).toUpperCase() +
                                 invitation?.role?.slice(1)
                              }`}
                              color='primary'
                              variant='outlined'
                           />
                           {invitation?.relationship && (
                              <Chip
                                 label={`Relationship: ${
                                    invitation?.relationship?.label ||
                                    invitation?.relationship?.type
                                 }`}
                                 color='info'
                                 variant='outlined'
                              />
                           )}
                           <Chip
                              label={`Expires: ${invitation?.expiresAt ? formatDate(invitation.expiresAt) : 'Never'}`}
                              color='default'
                              variant='outlined'
                           />
                        </Box>

                        {invitation?.status === 'expired' ? (
                           <Alert severity='error'>
                              This invitation has expired. Please request a new invitation.
                           </Alert>
                        ) : invitation?.status === 'accepted' ? (
                           <Alert severity='success'>
                              You have already accepted this invitation.
                           </Alert>
                        ) : invitation?.status === 'declined' ? (
                           <Alert severity='info'>
                              You have declined this invitation. Contact the organizer for a new
                              invitation if needed.
                           </Alert>
                        ) : (
                           <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                              <Button
                                 variant='contained'
                                 color='primary'
                                 fullWidth
                                 onClick={handleAcceptInvitation}
                                 disabled={processing}
                              >
                                 {processing ? 'Accepting...' : 'Accept Invitation'}
                              </Button>
                              <Button
                                 variant='outlined'
                                 color='error'
                                 fullWidth
                                 onClick={handleDeclineInvitation}
                                 disabled={processing}
                              >
                                 Decline
                              </Button>
                           </Box>
                        )}
                     </Stack>
                  </>
               )}
            </Paper>
         </Box>

         {/* Registration/Login Modal */}
         <Dialog
            open={registrationModalOpen}
            onClose={() => setRegistrationModalOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     {pendingAction === 'accept' ? 'Accept' : 'Decline'} Invitation
                  </Typography>
                  <IconButton onClick={() => setRegistrationModalOpen(false)}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your details to {pendingAction === 'accept' ? 'accept' : 'decline'} this
                  invitation.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
                  disabled
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setRegistrationModalOpen(false)}>Cancel</Button>
               <Button onClick={handleRegistration} variant='contained' disabled={submitting}>
                  {submitting ? <CircularProgress size={24} /> : 'Submit'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Container>
   );
};

export default AcceptInvitationPage;
